<template>
  <div
    ref="containerRef"
    class="feedback-container"
    :class="{ 'is-visible': uiStore.isVisualEffectRunning }"
    :style="{ 'pointer-events': isAnimationRunning ? 'auto' : 'none' }"
  ></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useUIStore } from '@/stores/uiStore';
// Import canvas-confetti. (Ensure it’s installed in your project.)
import confetti from 'canvas-confetti';

const containerRef = ref(null);
const uiStore = useUIStore();
// This will hold our confetti instance (bound to our canvas).
let confettiInstance = null;
let canvasElement = null;
const isAnimationRunning = ref(false);
const cleanupTimeout = ref(null);

// A helper to convert a duration (in seconds) to “ticks” (the confetti option)
// Adjust the multiplier (here 25) to change how long particles last.
function durationToTicks(duration) {
  return Math.floor(duration * 25);
}

function randomColor() {
  const colors = [
    '#e74c3c',
    '#f1c40f',
    '#2ecc71',
    '#3498db',
    '#9b59b6',
    '#e67e22',
    '#16a085',
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

/*
  Each animation function creates a confetti burst with settings
  inspired by the provided examples. You can tweak the numbers
  (particleCount, spread, startVelocity, etc.) to your taste.
*/

function animateStars(duration, intensity) {
  const config = {
    particleCount: Math.floor(40 * intensity),
    spread: 360,
    startVelocity: 30,
    ticks: durationToTicks(duration),
    gravity: 0, // No gravity so the stars float
    decay: 0.94,
    shapes: ['star'],
    colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8'],
    origin: { x: 0.5, y: 0.5 },
  };
  confettiInstance(config);
}

function animateFireworks(duration, intensity) {
  // Fireworks should be a burst of a single color.
  const config = {
    particleCount: Math.floor(80 * intensity),
    spread: 360,
    startVelocity: 30,
    ticks: durationToTicks(duration),
    gravity: 0.5,
    decay: 0.94,
    shapes: ['circle'],
    colors: [
      randomColor(),
      randomColor(),
      randomColor(),
      randomColor(),
      randomColor(),
    ], // Single-color burst
    origin: { x: 0.5, y: 0.5 },
  };
  confettiInstance(config);
}

function animateExplosions(duration, intensity) {
  const config = {
    particleCount: Math.floor(100 * intensity),
    spread: 180,
    startVelocity: 80,
    ticks: durationToTicks(duration),
    gravity: 0.8,
    decay: 0.94,
    shapes: ['circle', 'square'],
    colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8'],
    origin: { x: 0.5, y: 0.5 },
  };
  confettiInstance(config);
}

function animateSide(duration, intensity) {
  const config = {
    particleCount: Math.floor(60 * intensity),
    spread: 45, // Narrow spread for a directional burst
    startVelocity: 30,
    ticks: durationToTicks(duration),
    gravity: 0.5,
    decay: 0.94,
    shapes: ['square'],
    colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8'],
    // Launch confetti from the left side
    origin: { x: 0, y: Math.random() * 0.5 + 0.25 },
    particles: {
      size: {
        value: { min: 2, max: 200 }, // Particles will have a random size between 2 and 4
      },
    },
  };
  confettiInstance(config);

  // let's do a second burst from the right side
  const rightConfig = {
    particleCount: Math.floor(60 * intensity),
    spread: 45, // Narrow spread for a directional burst
    startVelocity: 30,
    ticks: durationToTicks(duration),
    gravity: 0.5,
    decay: 0.94,
    size: 500,
    shapes: ['square'],
    colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8'],
    // Launch confetti from the right side
    origin: { x: 1, y: Math.random() * 0.5 + 0.25 },
  };
  confettiInstance(rightConfig);
}

function animateSingle(duration, intensity) {
  const config = {
    particleCount: 1,
    spread: 0,
    startVelocity: 20,
    ticks: durationToTicks(duration),
    gravity: 0.5,
    decay: 0.94,
    shapes: ['circle'],
    colors: [randomColor()],
    origin: { x: 0.5, y: 0.5 },
  };
  confettiInstance(config);
}

function destroyCanvas() {
  if (confettiInstance) {
    confettiInstance.reset();
  }

  if (canvasElement && canvasElement.parentNode) {
    canvasElement.parentNode.removeChild(canvasElement);
  }

  canvasElement = null;
  confettiInstance = null;
}

function createCanvas() {
  // Remove existing canvas if it exists
  destroyCanvas();

  // Create fresh canvas element
  canvasElement = document.createElement('canvas');
  canvasElement.style.width = '100%';
  canvasElement.style.height = '100%';
  canvasElement.style.pointerEvents = 'none';
  canvasElement.style.position = 'absolute';
  canvasElement.style.top = '0';
  canvasElement.style.left = '0';
  canvasElement.style.zIndex = '-1'; // Put canvas behind everything

  if (containerRef.value) {
    containerRef.value.appendChild(canvasElement);
  }

  // Create fresh confetti instance
  confettiInstance = confetti.create(canvasElement, {
    resize: true,
    useWorker: false,
    disableForReducedMotion: true,
  });
}

async function runAnimation(type, duration = 2, intensity = 1) {
  // Clear any existing cleanup timeout
  if (cleanupTimeout.value) {
    clearTimeout(cleanupTimeout.value);
  }

  // Create fresh canvas for this animation
  createCanvas();

  isAnimationRunning.value = true;

  switch (type) {
    case 'stars':
      animateStars(duration, intensity);
      break;
    case 'fireworks':
      animateFireworks(duration, intensity);
      break;
    case 'explosions':
      animateExplosions(duration, intensity);
      break;
    case 'side':
      animateSide(duration, intensity);
      break;
    case 'single':
      animateSingle(duration, intensity);
      break;
    default:
      console.warn('Unknown animation type:', type);
  }

  // Set cleanup timeout - destroy canvas completely after animation
  const cleanupDuration = duration * 1000;

  cleanupTimeout.value = setTimeout(() => {
    isAnimationRunning.value = false;
    destroyCanvas(); // Completely remove canvas
  }, cleanupDuration);
}

onMounted(() => {
  // Don't create canvas on mount - create it fresh for each animation
  // Register the runAnimation function so that other parts of your app can trigger it.
  uiStore.registerAnimationComponent({ runAnimation });
});

// Cleanup on unmount
onUnmounted(() => {
  if (cleanupTimeout.value) {
    clearTimeout(cleanupTimeout.value);
  }
  destroyCanvas();
});

defineExpose({ runAnimation });
</script>

<style lang="scss" scoped>
.feedback-container {
  position: fixed; /* Use fixed instead of absolute for better positioning */
  inset: 0;
  overflow: hidden;
  z-index: 9999;
  height: 100vh;
  height: 100svh;
  height: 100dvh;
  width: 100vw;
  left: 0;
  top: 100vh; /* Start off-screen */
  top: 100svh;
  top: 100dvh;
  pointer-events: none !important; /* Force no pointer events */
  transition: top 0.3s ease-in-out; /* Smooth transition */

  &.is-visible {
    top: 0;
    transition: top 0.1s ease-in; /* Faster transition in */
  }

  /* Ensure canvas inside doesn't interfere with pointer events */
  canvas {
    pointer-events: none !important;
    position: absolute !important;
    z-index: -1 !important; /* Force canvas behind everything */
  }
}
</style>
